
import { BoundingBox, Edge, XYPos_$reflection, Edge_$reflection, XYPos } from "../Common/CommonTypes.js";
import { min, max } from "../fable_modules/fable-library-js.5.13.0/Double.js";
import { remove, fold as fold_2, toArray, map as map_2, add, tryFind, containsKey, toList, filter as filter_1, FSharpMap__get_Item } from "../fable_modules/fable-library-js.5.13.0/Map.js";
import { isEmpty, tryPick, allPairs, singleton, concat, unzip, cons, partition, splitAt, tryFind as tryFind_1, ofArray, append, indexed, sortDescending, mapIndexed, ofArrayWithTail, getSlice, empty, updateAt, removeManyAt, item, reverse, length as length_2, takeWhile, exists, map as map_1, collect, filter, fold } from "../fable_modules/fable-library-js.5.13.0/List.js";
import { defaultArg, value as value_2 } from "../fable_modules/fable-library-js.5.13.0/Option.js";
import { DrawModelType_SymbolT_symbols_, DrawModelType_BusWireT_symbol_, DrawModelType_SymbolT_ports_, DrawModelType_BusWireT_wires_, DrawModelType_SymbolT_Symbol, DrawModelType_BusWireT_WireType, DrawModelType_BusWireT_Model, DrawModelType_BusWireT_Wire_$reflection, DrawModelType_SymbolT_DirectedPortId, DrawModelType_SymbolT_Annotation, DrawModelType_BusWireT_Wire, DrawModelType_BusWireT_mode_, DrawModelType_BusWireT_segments_, DrawModelType_BusWireT_wireOf_, DrawModelType_BusWireT_Segment, DrawModelType_BusWireT_ASegment } from "../Model/DrawModelType.js";
import { printf, toFail } from "../fable_modules/fable-library-js.5.13.0/String.js";
import { makeInitialSegmentsList, logSegmentId, Constants_nubLength, Constants_modernCirclePositionTolerance } from "./BusWire.js";
import { partitionWiresIntoNets, Constants_intervalTolerance, getAbsSegments } from "./BlockHelpers.js";
import { MapModule_ValuesL } from "../Common/EEExtensions.js";
import { comparePrimitives, sign, equals } from "../fable_modules/fable-library-js.5.13.0/Util.js";
import { deletePortFromMaps, getPortLocation, getPortLocations } from "./Symbol.js";
import { Optic_Map, Optic_Map_op_HatPercent_Z1462312A, Compose_Lens, Compose_Lens_op_GreaterMinusGreater_Z15C92E89, Optic_Set, Optic_Set_op_HatEquals_Z147477F8 } from "../Common/Optics.js";
import { warn } from "../Shared/Log.js";
import { Record } from "../fable_modules/fable-library-js.5.13.0/Types.js";
import { int32_type, float64_type, record_type } from "../fable_modules/fable-library-js.5.13.0/Reflection.js";
import { fold as fold_1, map as map_3 } from "../fable_modules/fable-library-js.5.13.0/Array.js";
import { instrumentInterval, instrumentTime, getTimeMs } from "../Common/TimeHelpers.js";

/**
 * Returns Some distance between a point and a segment defined by a start and end XYPos,
 * and None if the segment is of 0 length (can't be clicked)
 */
export function distanceBetweenPointAndSegment(segStart, segEnd, point) {
    let p1_2, left_1, right_1, p2_2, left_2, right_2, diff_1, left_5, right_5, p1_4, p2_4;
    let matchValue;
    let diff;
    const left = segStart;
    const right = segEnd;
    diff = (new XYPos(left.X - right.X, left.Y - right.Y));
    const p1_1 = diff;
    const p2_1 = diff;
    matchValue = ((p1_1.X * p2_1.X) + (p1_1.Y * p2_1.Y));
    if (matchValue === 0) {
        return undefined;
    }
    else {
        let boundedProjection;
        const left_4 = segStart;
        let right_4;
        const factor = max(0, min(1, ((p1_2 = ((left_1 = point, (right_1 = segStart, new XYPos(left_1.X - right_1.X, left_1.Y - right_1.Y)))), (p2_2 = ((left_2 = segEnd, (right_2 = segStart, new XYPos(left_2.X - right_2.X, left_2.Y - right_2.Y)))), (p1_2.X * p2_2.X) + (p1_2.Y * p2_2.Y)))) / matchValue));
        let pos_1;
        const left_3 = segEnd;
        const right_3 = segStart;
        pos_1 = (new XYPos(left_3.X - right_3.X, left_3.Y - right_3.Y));
        right_4 = (new XYPos(factor * pos_1.X, factor * pos_1.Y));
        boundedProjection = (new XYPos(left_4.X + right_4.X, left_4.Y + right_4.Y));
        return Math.sqrt((diff_1 = ((left_5 = point, (right_5 = boundedProjection, new XYPos(left_5.X - right_5.X, left_5.Y - right_5.Y)))), (p1_4 = diff_1, (p2_4 = diff_1, (p1_4.X * p2_4.X) + (p1_4.Y * p2_4.Y)))));
    }
}

/**
 * Finds the closest non-zero-length segment in a wire to a mouse click using euclidean distance.
 * Return it and all overlapping same-direction same-net segments
 */
export function getClickedSegment(model, wireId, mouse) {
    let this$;
    let matchValue_4;
    const wire_2 = FSharpMap__get_Item(model.Wires, wireId);
    matchValue_4 = fold((tupledArg, seg_1) => {
        let segStart, segEnd, state, currDist, aSeg, minDist, minASeg;
        const currState = tupledArg[0];
        const currPos = tupledArg[1];
        const currOrientation = tupledArg[2];
        const nextOrientation = (currOrientation === "vertical") ? "horizontal" : "vertical";
        if (Math.abs(seg_1.Length) < 1E-07) {
            return [currState, currPos, nextOrientation];
        }
        else {
            let nextPos;
            const position = currPos;
            const length = seg_1.Length;
            nextPos = ((currOrientation === "vertical") ? (new XYPos(position.X, position.Y + length)) : (new XYPos(position.X + length, position.Y)));
            return [(segStart = currPos, (segEnd = nextPos, (state = currState, (currDist = value_2(distanceBetweenPointAndSegment(segStart, segEnd, mouse)), (aSeg = (new DrawModelType_BusWireT_ASegment(segStart, segEnd, seg_1)), (state == null) ? [aSeg, currDist] : ((minDist = state[1], (minASeg = state[0], (currDist < minDist) ? [aSeg, currDist] : [minASeg, minDist])))))))), nextPos, nextOrientation];
        }
    }, [undefined, wire_2.StartPos, wire_2.InitialOrientation], wire_2.Segments)[0];
    if (matchValue_4 == null) {
        return toFail(printf("getClosestSegment was given a wire with no segments"));
    }
    else {
        matchValue_4[1];
        const model_1 = model;
        const segment = matchValue_4[0];
        const wire = FSharpMap__get_Item(model_1.Wires, ((this$ = segment, [this$.Segment.Index, this$.Segment.WireId]))[1]);
        return filter((aSeg_1) => {
            let this$_2, delta, left, right, this$_3, delta_1, left_1, right_1;
            const d = distanceBetweenPointAndSegment(aSeg_1.Start, aSeg_1.End, mouse);
            const matchValue = Math.abs(aSeg_1.Segment.Length) < 1E-07;
            const matchValue_2 = ((this$_2 = segment, (delta = ((left = this$_2.Start, (right = this$_2.End, new XYPos(left.X - right.X, left.Y - right.Y)))), (Math.abs(delta.X) > Math.abs(delta.Y)) ? "horizontal" : "vertical"))) === ((this$_3 = aSeg_1, (delta_1 = ((left_1 = this$_3.Start, (right_1 = this$_3.End, new XYPos(left_1.X - right_1.X, left_1.Y - right_1.Y)))), (Math.abs(delta_1.X) > Math.abs(delta_1.Y)) ? "horizontal" : "vertical")));
            let matchResult, d_1;
            if (d != null) {
                if (matchValue) {
                    matchResult = 1;
                }
                else if (aSeg_1.Segment.Draggable) {
                    if (matchValue_2) {
                        matchResult = 0;
                        d_1 = d;
                    }
                    else {
                        matchResult = 1;
                    }
                }
                else {
                    matchResult = 1;
                }
            }
            else {
                matchResult = 1;
            }
            switch (matchResult) {
                case 0:
                    return d_1 < Constants_modernCirclePositionTolerance;
                default:
                    return false;
            }
        }, collect(getAbsSegments, MapModule_ValuesL(filter_1((wid_1, wire$0027) => equals(wire$0027.OutputPort, wire.OutputPort), model_1.Wires))));
    }
}

/**
 * Returns a list of all the wires in the given model
 */
export function getWireList(model) {
    return map_1((tuple) => tuple[1], toList(model.Wires));
}

/**
 * Returns the IDs of the wires in the model connected to a list of components given by compIds
 */
export function getConnectedWires(model, compIds) {
    return filter((wire) => {
        const patternInput = getPortLocations(model.Symbol, compIds);
        if (containsKey(wire.InputPort, patternInput[0])) {
            return true;
        }
        else {
            return containsKey(wire.OutputPort, patternInput[1]);
        }
    }, getWireList(model));
}

/**
 * Returns the IDs of the wires in the model connected to a list of components given by compIds
 */
export function getConnectedWireIds(model, compIds) {
    return map_1((wire) => wire.WId, getConnectedWires(model, compIds));
}

/**
 * Returns a list of wire IDs that meet the given condition
 */
export function getFilteredIdList(condition, wireLst) {
    return map_1((wire) => wire.WId, filter(condition, wireLst));
}

/**
 * A wire the user has routed by hand: at least one of its segments has been dragged, which is
 * what Manual means. Nothing else sets it - separation moves segments without marking them.
 */
export function isManuallyRouted(wire) {
    return exists((seg) => (seg.Mode === "manual"), wire.Segments);
}

/**
 * Given a model and a list of component Ids, returns an anonymous record
 * containing the id of wires connected to input ports, output ports or both
 */
export function filterWiresByCompMoved(model, compIds) {
    const wireList = getWireList(model);
    const patternInput = getPortLocations(model.Symbol, compIds);
    const containsInputPort = (wire) => containsKey(wire.InputPort, patternInput[0]);
    const containsOutputPort = (wire_1) => containsKey(wire_1.OutputPort, patternInput[1]);
    const inputWires = getFilteredIdList(containsInputPort, wireList);
    const outputWires = getFilteredIdList(containsOutputPort, wireList);
    return {
        Both: getFilteredIdList((wire_2) => {
            if (containsInputPort(wire_2)) {
                return containsOutputPort(wire_2);
            }
            else {
                return false;
            }
        }, wireList),
        Inputs: inputWires,
        Outputs: outputWires,
    };
}

/**
 * Returns a distance for a wire move that has been reduced if needed to enforce minimum first/last segment lengths.
 * These prevent the first non-zero segment perpendicular to the nubs
 * to be dragged closer than Constants.nubLength
 * TODO - this can maybe be simplified given we now coalesce segments
 */
export function getSafeDistanceForMove(segments, index, distance) {
    let f2, x;
    const findBindingSegments = (portIndex, segList) => takeWhile((seg) => {
        if ((seg.Index % 2) === (portIndex % 2)) {
            return true;
        }
        else {
            return seg.Length === 0;
        }
    }, segList);
    const reduceDistance = (bindingSegs, findBindingIndex, distance_1) => {
        if (findBindingIndex(bindingSegs) !== index) {
            return distance_1;
        }
        else {
            const dist_1 = fold((dist, seg_1) => (dist + seg_1.Length), 0, bindingSegs);
            if (sign(dist_1) === -1) {
                return max(distance_1, dist_1 + Constants_nubLength);
            }
            else {
                return min(distance_1, dist_1 - Constants_nubLength);
            }
        }
    };
    const bindingInputSegs = map_1((seg_2) => (new DrawModelType_BusWireT_Segment(seg_2.Index, -seg_2.Length, seg_2.WireId, seg_2.IntersectOrJumpList, seg_2.Draggable, seg_2.Mode)), findBindingSegments(0, segments));
    const findInputBindingIndex = (boundSegList_1) => (length_2(boundSegList_1) | 0);
    return reduceDistance(findBindingSegments(length_2(segments) - 1, reverse(segments)), (f2 = ((x = ((length_2(segments) - 1) | 0), (y) => ((x - y) | 0))), (arg) => (f2(findInputBindingIndex(arg)) | 0)), reduceDistance(bindingInputSegs, findInputBindingIndex, distance));
}

/**
 * Used when two segments can be coalesced by removing a zero segment separating
 * them
 */
export function removeZeroSegment(segs, indexToRemove) {
    let bind$0040;
    const index = indexToRemove | 0;
    const newLength = item(index + 1, segs).Length + item(index - 1, segs).Length;
    const source = removeManyAt(index, 2, segs);
    return updateAt(index - 1, (bind$0040 = item(index - 1, segs), new DrawModelType_BusWireT_Segment(bind$0040.Index, newLength, bind$0040.WireId, bind$0040.IntersectOrJumpList, bind$0040.Draggable, bind$0040.Mode)), source);
}

/**
 * After coalescing a wire the wire ends may no longer be draggable.
 * This function checks this and adds two segments to correct the problem
 * if necessary. The added segments will not alter wire appearance.
 * The transformation is:
 * BEFORE: 1st seg length x. AFTER: 1st segment length nubLength -> zero-length seg
 * -> segment length x - nubLength
 */
export function makeEndsDraggable(segments) {
    const addNubIfPossible = (segments_1) => {
        let this$;
        const seg0 = item(0, segments_1);
        if ((Math.abs(item(0, segments_1).Length) > (Constants_nubLength + 1E-07)) && ((length_2(segments_1) === 1) ? true : !((this$ = item(1, segments_1), Math.abs(this$.Length) < 1E-07)))) {
            const delta = sign(item(0, segments_1).Length) * Constants_nubLength;
            const newSeg1 = new DrawModelType_BusWireT_Segment(seg0.Index, 0, seg0.WireId, empty(), true, "auto");
            return ofArrayWithTail([new DrawModelType_BusWireT_Segment(seg0.Index, delta, seg0.WireId, seg0.IntersectOrJumpList, seg0.Draggable, seg0.Mode), newSeg1, new DrawModelType_BusWireT_Segment(newSeg1.Index, seg0.Length - delta, newSeg1.WireId, newSeg1.IntersectOrJumpList, newSeg1.Draggable, newSeg1.Mode)], getSlice(1, undefined, segments_1));
        }
        else {
            return segments_1;
        }
    };
    return mapIndexed((i, seg) => (new DrawModelType_BusWireT_Segment(i, seg.Length, seg.WireId, seg.IntersectOrJumpList, seg.Draggable, seg.Mode)), reverse(addNubIfPossible(reverse(addNubIfPossible(segments)))));
}

/**
 * If as the result of a drag a zero length segment separates two other draggable segments
 * the wire should be simplified by removing the zero-length segment and joining together the
 * draggables.
 */
export function coalesceInWire(wId, model) {
    let segments$0027, l;
    const segments = FSharpMap__get_Item(model.Wires, wId).Segments;
    let newSegments;
    const opposite = (seg1, seg2) => {
        const matchValue = sign(seg1.Length) | 0;
        const matchValue_1 = sign(seg2.Length) | 0;
        let matchResult;
        switch (matchValue) {
            case -1: {
                if (matchValue_1 === 1) {
                    matchResult = 0;
                }
                else {
                    matchResult = 1;
                }
                break;
            }
            case 1: {
                if (matchValue_1 === -1) {
                    matchResult = 0;
                }
                else {
                    matchResult = 1;
                }
                break;
            }
            default:
                matchResult = 1;
        }
        switch (matchResult) {
            case 0:
                return true;
            default:
                return false;
        }
    };
    newSegments = makeEndsDraggable((segments$0027 = fold(removeZeroSegment, segments, sortDescending(map_1((tupledArg_1) => (tupledArg_1[0] | 0), filter((tupledArg) => {
        let this$;
        const i = tupledArg[0] | 0;
        if (((((this$ = item(i, segments), Math.abs(this$.Length) < 1E-07)) && (i > 1)) && (i < (length_2(segments) - 2))) && item(i - 1, segments).Draggable) {
            return item(i + 1, segments).Draggable;
        }
        else {
            return false;
        }
    }, indexed(segments))), {
        Compare: (x, y) => (comparePrimitives(x, y) | 0),
    })), (opposite(item(0, segments), item(0, segments$0027)) ? true : opposite(item(length_2(segments) - 1, segments), item(length_2(segments$0027) - 1, segments$0027))) ? segments : segments$0027));
    return Optic_Set_op_HatEquals_Z147477F8(Optic_Set.Set$, (l = DrawModelType_BusWireT_wireOf_(wId), Compose_Lens_op_GreaterMinusGreater_Z15C92E89(Compose_Lens.Lens, DrawModelType_BusWireT_segments_)(l)))(newSegments)(model);
}

/**
 * If wire contains one or more manally routed segments return Some wire'
 * where wire' has all manual segments chnaged to auto.
 * Return None if no change is required (the normal case).
 */
export function resetWireToAutoKeepingPositionOpt(wire) {
    let f_1, mapping;
    if (exists((seg) => (seg.Mode === "manual"), wire.Segments)) {
        return ((f_1 = ((mapping = Optic_Map_op_HatPercent_Z1462312A(Optic_Map.Map$, DrawModelType_BusWireT_mode_)((_arg) => {
            if (_arg === "manual") {
                return "auto";
            }
            else {
                return _arg;
            }
        }), (list_1) => map_1(mapping, list_1))), Optic_Map_op_HatPercent_Z1462312A(Optic_Map.Map$, DrawModelType_BusWireT_segments_)(f_1)))(wire);
    }
    else {
        return undefined;
    }
}

/**
 * Returns a wwireOf_aining the updated list of segments after a segment is moved by
 * a specified distance. The moved segment is tagged as manual so that it is no longer auto-routed.
 * Throws an error if the index of the segment being moved is not a valid movable segment index.
 */
export function moveSegment(model, seg, distance) {
    const wire = FSharpMap__get_Item(model.Wires, seg.WireId);
    const segments = wire.Segments;
    const idx = seg.Index | 0;
    if ((idx <= 0) ? true : (idx >= (length_2(segments) - 1))) {
        warn(`segment ${seg.Index}:${logSegmentId(seg)} is out of range in a wire of ${length_2(segments)} segments`);
        return wire;
    }
    else {
        const safeDistance = getSafeDistanceForMove(segments, idx, distance);
        const prevSeg = item(idx - 1, segments);
        const nextSeg = item(idx + 1, segments);
        const movedSeg = item(idx, segments);
        const newPrevSeg = new DrawModelType_BusWireT_Segment(prevSeg.Index, prevSeg.Length + safeDistance, prevSeg.WireId, prevSeg.IntersectOrJumpList, prevSeg.Draggable, prevSeg.Mode);
        const newNextSeg = new DrawModelType_BusWireT_Segment(nextSeg.Index, nextSeg.Length - safeDistance, nextSeg.WireId, nextSeg.IntersectOrJumpList, nextSeg.Draggable, nextSeg.Mode);
        const newMovedSeg = new DrawModelType_BusWireT_Segment(movedSeg.Index, movedSeg.Length, movedSeg.WireId, movedSeg.IntersectOrJumpList, movedSeg.Draggable, "manual");
        return new DrawModelType_BusWireT_Wire(wire.WId, wire.InputPort, wire.OutputPort, wire.Color, wire.Width, append(getSlice(undefined, idx - 2, segments), append(ofArray([newPrevSeg, newMovedSeg, newNextSeg]), getSlice(idx + 2, undefined, segments))), wire.StartPos, wire.InitialOrientation);
    }
}

/**
 * Contains geometric information of a port
 */
export class PortInfo extends Record {
    constructor(Edge, Position) {
        super();
        this.Edge = Edge;
        this.Position = Position;
    }
}

export function PortInfo_$reflection() {
    return record_type("BusWireUpdateHelpers.PortInfo", [], PortInfo, () => [["Edge", Edge_$reflection()], ["Position", XYPos_$reflection()]]);
}

/**
 * Returns a function to rotate a segment list 90 degrees about the origin,
 * depending on its initial orientation
 */
export function rotateSegments90(initialOrientation) {
    return (arg) => map_1((tupledArg) => {
        let i;
        const seg = tupledArg[1];
        if ((i = (tupledArg[0] | 0), (initialOrientation === "vertical") ? ((i % 2) === 1) : ((i % 2) === 0))) {
            return new DrawModelType_BusWireT_Segment(seg.Index, -seg.Length, seg.WireId, seg.IntersectOrJumpList, seg.Draggable, seg.Mode);
        }
        else {
            return seg;
        }
    }, indexed(arg));
}

/**
 * Returns a version of the start and destination ports rotated until the start edge matches the target edge.
 */
export function rotateStartDest(target_mut, _arg1__mut, _arg1__1_mut) {
    rotateStartDest:
    while (true) {
        const target = target_mut, _arg1_ = _arg1__mut, _arg1__1 = _arg1__1_mut;
        let port, edge, port_1, edge_2;
        const _arg = [_arg1_, _arg1__1];
        const start = _arg[0];
        const dest = _arg[1];
        if (equals(start.Edge, target)) {
            return [start, dest];
        }
        else {
            target_mut = target;
            _arg1__mut = ((port = start, new PortInfo((edge = port.Edge, (edge.tag === 2) ? Edge.Bottom : ((edge.tag === 1) ? Edge.Right : ((edge.tag === 3) ? Edge.Top : Edge.Left))), new XYPos(port.Position.Y, -port.Position.X))));
            _arg1__1_mut = ((port_1 = dest, new PortInfo((edge_2 = port_1.Edge, (edge_2.tag === 2) ? Edge.Bottom : ((edge_2.tag === 1) ? Edge.Right : ((edge_2.tag === 3) ? Edge.Top : Edge.Left))), new XYPos(port_1.Position.Y, -port_1.Position.X))));
            continue rotateStartDest;
        }
        break;
    }
}

/**
 * Returns an anonymous record containing the starting symbol edge of a wire and its segment list that has been
 * rotated to a target symbol edge.
 */
export function rotateSegments(target_mut, wire_mut) {
    rotateSegments:
    while (true) {
        const target = target_mut, wire = wire_mut;
        let edge, edge_1;
        if (equals(wire.edge, target)) {
            return {
                edge: wire.edge,
                segments: wire.segments,
            };
        }
        else {
            const rotatedSegs = rotateSegments90((edge = wire.edge, (edge.tag === 1) ? "vertical" : ((edge.tag === 2) ? "horizontal" : ((edge.tag === 3) ? "horizontal" : "vertical"))))(wire.segments);
            target_mut = target;
            wire_mut = {
                edge: (edge_1 = wire.edge, (edge_1.tag === 2) ? Edge.Bottom : ((edge_1.tag === 1) ? Edge.Right : ((edge_1.tag === 3) ? Edge.Top : Edge.Left))),
                segments: rotatedSegs,
            };
            continue rotateSegments;
        }
        break;
    }
}

/**
 * The unit vector a wire takes leaving a port on this edge.
 */
export function outwardOf(edge) {
    switch (edge.tag) {
        case 3:
            return new XYPos(1, 0);
        case 0:
            return new XYPos(0, -1);
        case 1:
            return new XYPos(0, 1);
        default:
            return new XYPos(-1, 0);
    }
}

/**
 * How far a port lies INSIDE its own symbol's bounding box, measured along the direction a wire
 * leaves it by. Zero for a port that sits ON the box, which is nearly all of them.
 * 
 * A Mux2's Sel is what this is for. The symbol is drawn as a trapezium and the port is on the
 * sloping side, so the bounding box - which is what BOTH routing and separation use as the
 * obstacle - reaches nine units further out than the port does.
 * 
 * This is the whole of what the rest of the draw block needs to know about that. Routing and
 * separation both work in the space OUTSIDE the boxes, so a wire's end is taken to be where its
 * nub crosses the box and the first `inset` of that nub is treated as part of the symbol rather
 * than as wire. See `autoroute` below, where the two are converted, and `BusWireRoute` for what
 * it saves: with the wire's end on the box, a symbol's own edge stops it being pulled inside, and
 * no pass downstream needs a special case for a multiplexer.
 */
export function portInset(symModel, portId) {
    let left, right, matchValue_3, model, portId_1;
    const matchValue = tryFind(portId, symModel.Ports);
    if (matchValue != null) {
        const matchValue_1 = tryFind(matchValue.HostId, symModel.Symbols);
        if (matchValue_1 != null) {
            let box;
            const sym_1 = matchValue_1;
            let patternInput;
            const sym_2 = sym_1;
            const comp = sym_2.Component;
            const matchValue_2 = defaultArg(sym_2.HScale, 1);
            const vS = defaultArg(sym_2.VScale, 1);
            const hS = matchValue_2;
            const matchValue_2_1 = sym_2.STransform.Rotation;
            switch (matchValue_2_1.tag) {
                case 1:
                case 3: {
                    patternInput = [comp.W * hS, comp.H * vS];
                    break;
                }
                default:
                    patternInput = [comp.H * vS, comp.W * hS];
            }
            box = (equals(sym_1.Annotation, DrawModelType_SymbolT_Annotation.ScaleButton) ? (new BoundingBox((left = sym_1.Pos, (right = (new XYPos(9, 9)), new XYPos(left.X - right.X, left.Y - right.Y))), 17, 17)) : (new BoundingBox(sym_1.Pos, patternInput[1], patternInput[0])));
            const pos = getPortLocation(undefined, symModel, portId);
            return max(0, (matchValue_3 = ((model = symModel, (portId_1 = portId, FSharpMap__get_Item(FSharpMap__get_Item(model.Symbols, FSharpMap__get_Item(model.Ports, portId_1).HostId).PortMaps.Orientation, portId_1)))), (matchValue_3.tag === 3) ? ((box.TopLeft.X + box.W) - pos.X) : ((matchValue_3.tag === 0) ? (pos.Y - box.TopLeft.Y) : ((matchValue_3.tag === 1) ? ((box.TopLeft.Y + box.H) - pos.Y) : (pos.X - box.TopLeft.X)))));
        }
        else {
            return 0;
        }
    }
    else {
        return 0;
    }
}

/**
 * Give back the part of each end nub that a route worked out in box-edge space left out: the
 * first segment grows away from its port and the last one grows into its port, so each grows in
 * the direction it already runs. A nub is never zero length, so its sign is always meaningful.
 */
export function extendNubsToPorts(startInset, endInset, segments) {
    const grow = (inset, seg) => {
        if (inset <= 0) {
            return seg;
        }
        else {
            return new DrawModelType_BusWireT_Segment(seg.Index, seg.Length + (sign(seg.Length) * inset), seg.WireId, seg.IntersectOrJumpList, seg.Draggable, seg.Mode);
        }
    };
    const last = (length_2(segments) - 1) | 0;
    return mapIndexed((i, seg_1) => {
        if (i === 0) {
            return grow(startInset, seg_1);
        }
        else if (i === last) {
            return grow(endInset, seg_1);
        }
        else {
            return seg_1;
        }
    }, segments);
}

/**
 * Returns a newly autorouted version of a wire for the given model.
 * 
 * The route is worked out between the points where the two nubs CROSS their symbols' bounding
 * boxes, and the end nubs are then lengthened to reach the ports themselves. For nearly every
 * port those are the same point and this does nothing.
 * 
 * Where they are not - a Mux2's SEL sits nine units inside its box - it is what keeps every pass
 * downstream honest. The alternative, routing from the port and asking routing and separation to
 * understand that the first nine units of the wire are inside a symbol they are meant to avoid,
 * was tried: the intersection test needed a multiplexer exemption, separation needed its own, and
 * the two disagreed about which side of the box edge a segment sitting exactly ON it was - which
 * is how a wire arriving at a SEL port came to be drawn through the multiplexer. In this frame
 * the wire's end is ON the box, the room between the two boxes is what `roomForNub` measures, and
 * a symbol's edge stops its own wires being pulled inside it like anyone else's.
 */
export function autoroute(model, wire) {
    let portId_1, portId_5, left, right, pos, scaleFactor, left_1, right_1, pos_1, scaleFactor_1, edge_2;
    let patternInput;
    const model_1 = model.Symbol;
    patternInput = [getPortLocation(undefined, model_1, (portId_1 = (new DrawModelType_SymbolT_DirectedPortId(/* InputId */ 0, [wire.InputPort])), (portId_1.tag === 1) ? portId_1.fields[0] : portId_1.fields[0])), getPortLocation(undefined, model_1, (portId_5 = (new DrawModelType_SymbolT_DirectedPortId(/* OutputId */ 1, [wire.OutputPort])), (portId_5.tag === 1) ? portId_5.fields[0] : portId_5.fields[0]))];
    const startPos = patternInput[1];
    let destEdge;
    const model_6 = model.Symbol;
    let portId_13;
    const portId_10 = new DrawModelType_SymbolT_DirectedPortId(/* InputId */ 0, [wire.InputPort]);
    portId_13 = ((portId_10.tag === 1) ? portId_10.fields[0] : portId_10.fields[0]);
    destEdge = FSharpMap__get_Item(FSharpMap__get_Item(model_6.Symbols, FSharpMap__get_Item(model_6.Ports, portId_13).HostId).PortMaps.Orientation, portId_13);
    let startEdge;
    const model_9 = model.Symbol;
    let portId_19;
    const portId_16 = new DrawModelType_SymbolT_DirectedPortId(/* OutputId */ 1, [wire.OutputPort]);
    portId_19 = ((portId_16.tag === 1) ? portId_16.fields[0] : portId_16.fields[0]);
    startEdge = FSharpMap__get_Item(FSharpMap__get_Item(model_9.Symbols, FSharpMap__get_Item(model_9.Ports, portId_19).HostId).PortMaps.Orientation, portId_19);
    const startInset = portInset(model.Symbol, wire.OutputPort);
    const endInset = portInset(model.Symbol, wire.InputPort);
    const patternInput_1 = rotateStartDest(Edge.Right, new PortInfo(startEdge, (left = startPos, (right = ((pos = outwardOf(startEdge), (scaleFactor = startInset, new XYPos(pos.X * scaleFactor, pos.Y * scaleFactor)))), new XYPos(left.X + right.X, left.Y + right.Y)))), new PortInfo(destEdge, (left_1 = patternInput[0], (right_1 = ((pos_1 = outwardOf(destEdge), (scaleFactor_1 = endInset, new XYPos(pos_1.X * scaleFactor_1, pos_1.Y * scaleFactor_1)))), new XYPos(left_1.X + right_1.X, left_1.Y + right_1.Y)))));
    const normEnd = patternInput_1[1];
    return new DrawModelType_BusWireT_Wire(wire.WId, wire.InputPort, wire.OutputPort, wire.Color, wire.Width, extendNubsToPorts(startInset, endInset, rotateSegments(startEdge, {
        edge: Edge.Right,
        segments: makeInitialSegmentsList(wire.WId, patternInput_1[0].Position, normEnd.Position, normEnd.Edge),
    }).segments), startPos, (edge_2 = startEdge, (edge_2.tag === 1) ? "vertical" : ((edge_2.tag === 2) ? "horizontal" : ((edge_2.tag === 3) ? "horizontal" : "vertical"))));
}

/**
 * Returns an anonymous record indicating the position of pos relative to origin.
 * The isAbove field indicates whether pos is above (true) or below (false) origin.
 * The isLeft field indicates whether pos is to the left (true) or to the right (false) of origin.
 */
export function relativePosition(origin, pos) {
    return {
        isAbove: origin.Y > pos.Y,
        isLeft: origin.X > pos.X,
    };
}

/**
 * Returns the tuple (startPos, endPos) of the segment at the target index in the given wire.
 * Throws an error if the target index isn't found.
 */
export function getAbsoluteSegmentPos(wire, target) {
    let _arg_2;
    const wire_2 = wire;
    _arg_2 = fold((tupledArg, seg_1) => {
        const currPos = tupledArg[1];
        const currOrientation = tupledArg[2];
        let nextPos;
        const position = currPos;
        const length = seg_1.Length;
        nextPos = ((currOrientation === "vertical") ? (new XYPos(position.X, position.Y + length)) : (new XYPos(position.X + length, position.Y)));
        const nextOrientation = (currOrientation === "vertical") ? "horizontal" : "vertical";
        return [(seg_1.Index === target) ? [currPos, nextPos] : tupledArg[0], nextPos, nextOrientation];
    }, [undefined, wire_2.StartPos, wire_2.InitialOrientation], wire_2.Segments)[0];
    if (_arg_2 != null) {
        return _arg_2;
    }
    else {
        return toFail(`Couldn't find index ${target} in wire`);
    }
}

/**
 * Returns the length to change a segment represented by startPos -> endPos
 * in the appropriate dimension of the difference vector.
 */
export function getLengthDiff(difference, startPos, endPos) {
    let matchValue;
    const segStart = startPos;
    const segEnd = endPos;
    matchValue = ((Math.abs(segStart.X - segEnd.X) < 1E-07) ? "vertical" : ((Math.abs(segStart.Y - segEnd.Y) < 1E-07) ? "horizontal" : toFail(printf("ERROR: Diagonal wire"))));
    if (matchValue === "vertical") {
        return difference.Y;
    }
    else {
        return difference.X;
    }
}

/**
 * Given a segment list, returns the first manual segment index
 */
export function getManualIndex(segList) {
    let option_3;
    const option_1 = tryFind_1((seg) => (seg.Mode === "manual"), segList);
    option_3 = ((option_1 != null) ? option_1.Index : undefined);
    if (option_3 != null) {
        const index = option_3 | 0;
        if ((index < 1) ? true : (index >= (length_2(segList) - 1))) {
            return undefined;
        }
        else {
            return index;
        }
    }
    else {
        return undefined;
    }
}

/**
 * Gets the start position for partial routing.
 */
export function getPartialRouteStart(wire, manualIndex) {
    let option_3, option_1;
    return defaultArg((option_3 = ((option_1 = tryFind_1((seg) => (seg.Index === (manualIndex - 1)), wire.Segments), (option_1 != null) ? option_1.Index : undefined)), (option_3 != null) ? getAbsoluteSegmentPos(wire, option_3)[0] : undefined), wire.StartPos);
}

/**
 * Partitions a segment list into sections 3 sections for partial autorouting
 */
export function partitionSegments(segs, manualIdx) {
    const patternInput = (manualIdx === 1) ? [empty(), segs] : splitAt(manualIdx - 1, segs);
    const start = patternInput[0];
    const patternInput_1 = splitAt(2, patternInput[1]);
    const remaining = patternInput_1[1];
    const changed = patternInput_1[0];
    if (length_2(append(start, append(changed, remaining))) !== length_2(segs)) {
        warn(`bad partial routing partition: index=${manualIdx}:${length_2(start)},${length_2(changed)},${length_2(remaining)} (${length_2(segs)})`);
    }
    return [start, changed, remaining];
}

/**
 * Returns None if full autoroute is required or applies partial autorouting
 * from the start of the wire at newPortPos to the first manually routed segment
 * and returns Some wire with the new segments.
 */
export function partialAutoroute(model, wire, newPortPos, reversed) {
    let wire_1, matchValue_1, left, right, wire_2, matchValue_1_1, wire_3;
    const segs = wire.Segments;
    const newWire = new DrawModelType_BusWireT_Wire(wire.WId, wire.InputPort, wire.OutputPort, wire.Color, wire.Width, wire.Segments, newPortPos, wire.InitialOrientation);
    let option_5;
    let option_3;
    const option_1 = getManualIndex(segs);
    if (option_1 != null) {
        const manualIdx = option_1 | 0;
        const oldStartPos = getPartialRouteStart(wire, manualIdx);
        const newStartPos = getPartialRouteStart(newWire, manualIdx);
        const fixedPoint = getAbsoluteSegmentPos(wire, manualIdx)[1];
        const relativeToFixed = (pos) => relativePosition(fixedPoint, pos);
        let portOrientation;
        const model_2 = model.Symbol;
        let portId_5;
        const portId_2 = reversed ? (new DrawModelType_SymbolT_DirectedPortId(/* InputId */ 0, [wire.InputPort])) : (new DrawModelType_SymbolT_DirectedPortId(/* OutputId */ 1, [wire.OutputPort]));
        portId_5 = ((portId_2.tag === 1) ? portId_2.fields[0] : portId_2.fields[0]);
        portOrientation = FSharpMap__get_Item(FSharpMap__get_Item(model_2.Symbols, FSharpMap__get_Item(model_2.Ports, portId_5).HostId).PortMaps.Orientation, portId_5);
        option_3 = ((equals((wire_1 = wire, (matchValue_1 = (item(0, wire_1.Segments).Length > 0), (wire_1.InitialOrientation === "vertical") ? (matchValue_1 ? Edge.Bottom : Edge.Top) : (matchValue_1 ? Edge.Right : Edge.Left))), portOrientation) && equals(relativeToFixed(newStartPos), relativeToFixed(oldStartPos))) ? [manualIdx, (left = newStartPos, (right = oldStartPos, new XYPos(left.X - right.X, left.Y - right.Y))), portOrientation] : undefined);
    }
    else {
        option_3 = undefined;
    }
    if (option_3 != null) {
        const tupledArg = option_3;
        const patternInput = partitionSegments(segs, tupledArg[0]);
        const wire$0027 = new DrawModelType_BusWireT_Wire(wire.WId, wire.InputPort, wire.OutputPort, wire.Color, wire.Width, append(patternInput[0], append(map_1((seg) => {
            const patternInput_1 = getAbsoluteSegmentPos(wire, seg.Index);
            return new DrawModelType_BusWireT_Segment(seg.Index, seg.Length - getLengthDiff(tupledArg[1], patternInput_1[0], patternInput_1[1]), seg.WireId, seg.IntersectOrJumpList, seg.Draggable, seg.Mode);
        }, patternInput[1]), patternInput[2])), newPortPos, wire.InitialOrientation);
        option_5 = (equals((wire_2 = wire$0027, (matchValue_1_1 = (item(0, wire_2.Segments).Length > 0), (wire_2.InitialOrientation === "vertical") ? (matchValue_1_1 ? Edge.Bottom : Edge.Top) : (matchValue_1_1 ? Edge.Right : Edge.Left))), tupledArg[2]) ? wire$0027 : undefined);
    }
    else {
        option_5 = undefined;
    }
    if (option_5 != null) {
        return (wire_3 = option_5, new DrawModelType_BusWireT_Wire(wire_3.WId, wire_3.InputPort, wire_3.OutputPort, wire_3.Color, wire_3.Width, makeEndsDraggable(wire_3.Segments), wire_3.StartPos, wire_3.InitialOrientation));
    }
    else {
        return undefined;
    }
}

/**
 * Reverses a wire so that it may be processed in the opposite direction. This function is self-inverse.
 */
export function reverseWire(wire) {
    let this$, wire_2, this$_1, matchValue;
    return new DrawModelType_BusWireT_Wire(wire.WId, wire.InputPort, wire.OutputPort, wire.Color, wire.Width, mapIndexed((i, seg) => (new DrawModelType_BusWireT_Segment(i, -seg.Length, seg.WireId, seg.IntersectOrJumpList, seg.Draggable, seg.Mode)), reverse(wire.Segments)), (this$ = wire, (wire_2 = this$, fold((tupledArg, seg_1) => {
        const currOrientation = tupledArg[2];
        let nextPos;
        const position = tupledArg[1];
        const length = seg_1.Length;
        nextPos = ((currOrientation === "vertical") ? (new XYPos(position.X, position.Y + length)) : (new XYPos(position.X + length, position.Y)));
        return [nextPos, nextPos, (currOrientation === "vertical") ? "horizontal" : "vertical"];
    }, [this$.StartPos, wire_2.StartPos, wire_2.InitialOrientation], wire_2.Segments)[0])), (this$_1 = wire, (matchValue = ((length_2(this$_1.Segments) % 2) | 0), (matchValue === 1) ? this$_1.InitialOrientation : ((this$_1.InitialOrientation === "horizontal") ? "vertical" : "horizontal"))));
}

/**
 * Returns an updated wireMap with the IntersectOrJumpList of targetSeg
 * replaced by jumps or modern intersections.
 */
export function updateSegmentJumpsOrIntersections(targetSeg, intersectOrJump, wireMap) {
    let bind$0040;
    const wId = targetSeg.WireId;
    return add(wId, (bind$0040 = FSharpMap__get_Item(wireMap, wId), new DrawModelType_BusWireT_Wire(bind$0040.WId, bind$0040.InputPort, bind$0040.OutputPort, bind$0040.Color, bind$0040.Width, updateAt(targetSeg.Index, new DrawModelType_BusWireT_Segment(targetSeg.Index, targetSeg.Length, targetSeg.WireId, intersectOrJump, targetSeg.Draggable, targetSeg.Mode), FSharpMap__get_Item(wireMap, wId).Segments), bind$0040.StartPos, bind$0040.InitialOrientation)), wireMap);
}

/**
 * type used internally by modern wire circle calculation code
 * For a horizontal segment (x1,y) -> (x2,y):
 * P = y, Qmin = min y1 y2, Qmax = max y1 y2
 * For a vertical segment (x,y1) -> (x,y2) x & y are reversed, so P = x etc.
 * TODO - replace Index by Segment.
 */
export class SegInfo extends Record {
    constructor(P, Qmin, Qmax, Index, OfWire) {
        super();
        this.P = P;
        this.Qmin = Qmin;
        this.Qmax = Qmax;
        this.Index = (Index | 0);
        this.OfWire = OfWire;
    }
}

export function SegInfo_$reflection() {
    return record_type("BusWireUpdateHelpers.SegInfo", [], SegInfo, () => [["P", float64_type], ["Qmin", float64_type], ["Qmax", float64_type], ["Index", int32_type], ["OfWire", DrawModelType_BusWireT_Wire_$reflection()]]);
}

/**
 * get segments on wire partitioned horizontal and vertical.
 * small length segments are not included, since this is to determine modern circle placement
 */
export function getHVSegs(wire) {
    const makeInfo = (p, q1, q2, i, seg_1) => (new SegInfo(p, min(q1, q2), max(q1, q2), seg_1.Segment.Index, wire));
    const tupledArg = partition((seg) => {
        const index = seg.Segment.Index | 0;
        if (wire.InitialOrientation === "vertical") {
            return (index % 2) === 1;
        }
        else {
            return (index % 2) === 0;
        }
    }, filter((seg_2) => (Math.abs(seg_2.Segment.Length) > Constants_modernCirclePositionTolerance), getAbsSegments(wire)));
    return [map_1((seg_3) => makeInfo(seg_3.Start.Y, seg_3.Start.X, seg_3.End.X, seg_3.Segment.Index, seg_3), tupledArg[0]), map_1((seg_4) => makeInfo(seg_4.Start.X, seg_4.Start.Y, seg_4.End.Y, seg_4.Segment.Index, seg_4), tupledArg[1])];
}

export function resetWireJumpsOrIntersections(wire) {
    return new DrawModelType_BusWireT_Wire(wire.WId, wire.InputPort, wire.OutputPort, wire.Color, wire.Width, map_1((seg) => (new DrawModelType_BusWireT_Segment(seg.Index, seg.Length, seg.WireId, empty(), seg.Draggable, seg.Mode)), wire.Segments), wire.StartPos, wire.InitialOrientation);
}

export function resetModelJumpsOrIntersections(model) {
    return new DrawModelType_BusWireT_Model(model.Symbol, map_2((_arg, w) => resetWireJumpsOrIntersections(w), model.Wires), model.CopiedWires, model.SelectedSegment, model.LastMousePos, model.ErrorWires, model.Notifications, model.Type, model.ArrowDisplay, model.SnapToNet);
}

export function updateCirclesOnSegments(wiresToUpdate, circles, model) {
    return new DrawModelType_BusWireT_Model(model.Symbol, fold((wires, wire) => {
        const wire_1 = FSharpMap__get_Item(wires, wire.WId);
        return add(wire_1.WId, fold((wire_2, tupledArg) => {
            const cIndex = tupledArg[1] | 0;
            const seg = item(cIndex, wire_2.Segments);
            return new DrawModelType_BusWireT_Wire(wire_2.WId, wire_2.InputPort, wire_2.OutputPort, wire_2.Color, wire_2.Width, updateAt(cIndex, new DrawModelType_BusWireT_Segment(seg.Index, seg.Length, seg.WireId, cons(tupledArg[0], seg.IntersectOrJumpList), seg.Draggable, seg.Mode), wire_2.Segments), wire_2.StartPos, wire_2.InitialOrientation);
        }, wire_1, filter((_arg) => equals(_arg[2].WId, wire_1.WId), circles)), wires);
    }, model.Wires, wiresToUpdate), model.CopiedWires, model.SelectedSegment, model.LastMousePos, model.ErrorWires, model.Notifications, model.Type, model.ArrowDisplay, model.SnapToNet);
}

/**
 * Update all the modern routing circles on the net of wires: wiresInNet
 */
export function updateCirclesOnNet(model, wiresInNet) {
    let patternInput;
    const tupledArg = unzip(map_1(getHVSegs, wiresInNet));
    patternInput = [concat(tupledArg[0]), concat(tupledArg[1])];
    const vsL = patternInput[1];
    const hsL = patternInput[0];
    const getJoins = (segs) => collect((tupledArg_2) => {
        const s1 = tupledArg_2[0];
        const s2 = tupledArg_2[1];
        if ((Math.abs(s1.P - s2.P) < Constants_modernCirclePositionTolerance) && (Math.abs(s1.Qmax - s2.Qmin) < Constants_modernCirclePositionTolerance)) {
            return singleton({
                Index: s2.Index,
                P: (s1.P + s2.P) / 2,
                Q: (s1.Qmax + s2.Qmin) / 2,
                Wire: s2.OfWire,
            });
        }
        else {
            return empty();
        }
    }, allPairs(segs, segs));
    const intersectCircles = collect((tupledArg_1) => {
        let x, x_1, x_2, x_3;
        const h = tupledArg_1[0];
        const v = tupledArg_1[1];
        if ((((x = h.P, ((v.Qmin + Constants_intervalTolerance) < x) && (x < (v.Qmax - Constants_intervalTolerance)))) && ((x_1 = v.P, ((h.Qmin - Constants_intervalTolerance) < x_1) && (x_1 < (h.Qmax + Constants_intervalTolerance))))) ? true : (((x_2 = h.P, ((v.Qmin - Constants_intervalTolerance) < x_2) && (x_2 < (v.Qmax + Constants_intervalTolerance)))) && ((x_3 = v.P, ((h.Qmin + Constants_intervalTolerance) < x_3) && (x_3 < (h.Qmax - Constants_intervalTolerance)))))) {
            return singleton([v.P, h.Index, h.OfWire]);
        }
        else {
            return empty();
        }
    }, allPairs(hsL, vsL));
    const hJoinCircles = collect((join) => {
        if (exists((vs) => ((Math.abs(vs.P - join.Q) < Constants_modernCirclePositionTolerance) && ((Math.abs(vs.Qmin - join.P) < Constants_modernCirclePositionTolerance) ? true : (Math.abs(vs.Qmax - join.P) < Constants_modernCirclePositionTolerance))), vsL)) {
            return singleton([join.Q, join.Index, join.Wire]);
        }
        else {
            return empty();
        }
    }, getJoins(hsL));
    return updateCirclesOnSegments(wiresInNet, append(intersectCircles, append(collect((join_1) => defaultArg(tryPick((hs) => {
        if ((Math.abs(hs.P - join_1.Q) < Constants_modernCirclePositionTolerance) && ((Math.abs(hs.Qmin - join_1.P) < Constants_modernCirclePositionTolerance) ? true : (Math.abs(hs.Qmax - join_1.P) < Constants_modernCirclePositionTolerance))) {
            return singleton([join_1.P, hs.Index, hs.OfWire]);
        }
        else {
            return undefined;
        }
    }, hsL), empty()), getJoins(vsL)), hJoinCircles)), model);
}

/**
 * Update all the modern routing circles in the model
 */
export function updateCirclesOnAllNets(model) {
    const cleanModel = resetModelJumpsOrIntersections(model);
    return fold(updateCirclesOnNet, cleanModel, map_1((list_1) => map_1((tuple_1) => tuple_1[1], list_1), map_1((tuple) => tuple[1], partitionWiresIntoNets(cleanModel))));
}

/**
 * Returns a model with all the jumps updated
 */
export function makeAllJumps(wiresWithNoJumps, model) {
    const wires = map_3((tuple) => tuple[1], toArray(model.Wires));
    const matchValue_10 = model.Type;
    switch (matchValue_10.tag) {
        case 1:
            return updateCirclesOnAllNets(model);
        case 0:
            return model;
        default:
            return new DrawModelType_BusWireT_Model(model.Symbol, fold_1((map, wire_2) => {
                const wire_3 = wire_2;
                return fold((tupledArg_2, seg_4) => {
                    let segStart, segEnd, wireMap, seg, segStart_1, segEnd_1, jumpsOrIntersections_1;
                    const currPos_1 = tupledArg_2[1];
                    const currOrientation_1 = tupledArg_2[2];
                    let nextPos_1;
                    const position_1 = currPos_1;
                    const length_1 = seg_4.Length;
                    nextPos_1 = ((currOrientation_1 === "vertical") ? (new XYPos(position_1.X, position_1.Y + length_1)) : (new XYPos(position_1.X + length_1, position_1.Y)));
                    const nextOrientation_1 = (currOrientation_1 === "vertical") ? "horizontal" : "vertical";
                    return [(segStart = currPos_1, (segEnd = nextPos_1, (wireMap = tupledArg_2[0], (seg = seg_4, (((segStart_1 = segStart, (segEnd_1 = segEnd, (Math.abs(segStart_1.X - segEnd_1.X) < 1E-07) ? "vertical" : ((Math.abs(segStart_1.Y - segEnd_1.Y) < 1E-07) ? "horizontal" : toFail(printf("ERROR: Diagonal wire")))))) === "horizontal") ? ((jumpsOrIntersections_1 = fold_1((jumpsOrIntersections, wire) => {
                        let wire_1;
                        if (equals(model.Type, DrawModelType_BusWireT_WireType.Jump)) {
                            return append(jumpsOrIntersections, ((wire_1 = wire, fold((tupledArg, seg_3) => {
                                let segStart_3, segEnd_3, state_1, segStart_4, segEnd_4, xVStart, xHStart, xHEnd, yVStart, yVEnd, yHEnd, matchValue_6, matchValue_7, matchValue_8, matchValue_9;
                                const currPos = tupledArg[1];
                                const currOrientation = tupledArg[2];
                                let nextPos;
                                const position = currPos;
                                const length = seg_3.Length;
                                nextPos = ((currOrientation === "vertical") ? (new XYPos(position.X, position.Y + length)) : (new XYPos(position.X + length, position.Y)));
                                const nextOrientation = (currOrientation === "vertical") ? "horizontal" : "vertical";
                                return [(segStart_3 = currPos, (segEnd_3 = nextPos, (state_1 = tupledArg[0], (((segStart_4 = segStart_3, (segEnd_4 = segEnd_3, (Math.abs(segStart_4.X - segEnd_4.X) < 1E-07) ? "vertical" : ((Math.abs(segStart_4.Y - segEnd_4.Y) < 1E-07) ? "horizontal" : toFail(printf("ERROR: Diagonal wire")))))) === "vertical") ? ((xVStart = segStart_3.X, (xHStart = state_1.Start.X, (xHEnd = state_1.End.X, (yVStart = segStart_3.Y, (yVEnd = segEnd_3.Y, (yHEnd = state_1.End.Y, (matchValue_6 = max(xHStart, xHEnd), (matchValue_7 = min(xHStart, xHEnd), (matchValue_8 = max(yVStart, yVEnd), (matchValue_9 = min(yVStart, yVEnd), ((((xVStart < matchValue_6) && (xVStart > matchValue_7)) && (yHEnd < matchValue_8)) && (yHEnd > matchValue_9)) ? {
                                    End: state_1.End,
                                    JumpsOrIntersections: cons(Math.abs(xVStart - xHStart), state_1.JumpsOrIntersections),
                                    Start: state_1.Start,
                                } : state_1))))))))))) : state_1))), nextPos, nextOrientation];
                            }, [{
                                End: segEnd,
                                JumpsOrIntersections: empty(),
                                Start: segStart,
                            }, wire_1.StartPos, wire_1.InitialOrientation], wire_1.Segments)[0])).JumpsOrIntersections);
                        }
                        else {
                            return jumpsOrIntersections;
                        }
                    }, empty(), wires), !equals(jumpsOrIntersections_1, seg.IntersectOrJumpList) ? updateSegmentJumpsOrIntersections(seg, jumpsOrIntersections_1, wireMap) : wireMap)) : wireMap)))), nextPos_1, nextOrientation_1];
                }, [map, wire_3.StartPos, wire_3.InitialOrientation], wire_3.Segments)[0];
            }, model.Wires, wires), model.CopiedWires, model.SelectedSegment, model.LastMousePos, model.ErrorWires, model.Notifications, model.Type, model.ArrowDisplay, model.SnapToNet);
    }
}

export function updateWireSegmentJumps(wireList, model) {
    const startT = getTimeMs();
    const model_1 = makeAllJumps(empty(), model);
    instrumentTime("UpdateJumps", startT);
    return model_1;
}

export function resetJumpsOrIntersections(wire) {
    return new DrawModelType_BusWireT_Wire(wire.WId, wire.InputPort, wire.OutputPort, wire.Color, wire.Width, map_1((seg) => (new DrawModelType_BusWireT_Segment(seg.Index, seg.Length, seg.WireId, empty(), seg.Draggable, seg.Mode)), wire.Segments), wire.StartPos, wire.InitialOrientation);
}

export function resetJumps(model) {
    return new DrawModelType_BusWireT_Model(model.Symbol, fold_2((wires, wid, wire) => add(wid, resetJumpsOrIntersections(wire), wires), model.Wires, model.Wires), model.CopiedWires, model.SelectedSegment, model.LastMousePos, model.ErrorWires, model.Notifications, model.Type, model.ArrowDisplay, model.SnapToNet);
}

/**
 * This function updates the wire model by removing from the stored lists of intersections
 * all those generated by wireList wires.
 * intersetcions are stored in maps on the model and on the horizontal segments containing the jumps
 */
export function resetWireSegmentJumps(wireList, model) {
    return instrumentInterval("ResetJumps", getTimeMs(), resetJumps(model));
}

export function deleteWiresWithPort(delPorts, model) {
    if (isEmpty(delPorts)) {
        return model;
    }
    else {
        const wires = toList(model.Wires);
        const patternInput = fold((tupledArg, p) => {
            let option_1, sym;
            const ports = tupledArg[0];
            const symbols = tupledArg[1];
            const conns = tupledArg[2];
            if (p == null) {
                return [ports, symbols, conns];
            }
            else {
                const port = p;
                const localConns = map_1((tuple) => tuple[0], filter((tupledArg_1) => {
                    const wire = tupledArg_1[1];
                    if (equals(wire.InputPort, port.Id)) {
                        return true;
                    }
                    else {
                        return equals(wire.OutputPort, port.Id);
                    }
                }, wires));
                const symbols_1 = defaultArg((option_1 = tryFind(port.HostId, symbols), (option_1 != null) ? ((sym = option_1, add(port.HostId, new DrawModelType_SymbolT_Symbol(sym.Pos, sym.CentrePos, sym.OffsetFromBBCentre, sym.InWidth0, sym.InWidth1, sym.InWidths, sym.LabelBoundingBox, sym.LabelHasDefaultPos, sym.LabelRotation, sym.Appearance, sym.Id, sym.Component, sym.Annotation, sym.Moving, sym.IsClocked, sym.STransform, sym.ReversedInputPorts, deletePortFromMaps(port.Id, sym.PortMaps), sym.HScale, sym.VScale, sym.MovingPort, sym.MovingPortTarget, sym.ArrayText), symbols))) : undefined), symbols);
                return [remove(port.Id, ports), symbols_1, append(conns, localConns)];
            }
        }, [model.Symbol.Ports, model.Symbol.Symbols, empty()], delPorts);
        return Optic_Map_op_HatPercent_Z1462312A(Optic_Map.Map$, DrawModelType_BusWireT_wires_)((wires_1) => fold((wires_2, connId_1) => remove(connId_1, wires_2), wires_1, patternInput[2]))(Optic_Set_op_HatEquals_Z147477F8(Optic_Set.Set$, Compose_Lens_op_GreaterMinusGreater_Z15C92E89(Compose_Lens.Lens, DrawModelType_SymbolT_ports_)(DrawModelType_BusWireT_symbol_))(patternInput[0])(Optic_Set_op_HatEquals_Z147477F8(Optic_Set.Set$, Compose_Lens_op_GreaterMinusGreater_Z15C92E89(Compose_Lens.Lens, DrawModelType_SymbolT_symbols_)(DrawModelType_BusWireT_symbol_))(patternInput[1])(model)));
    }
}

